import { Group, Stack, TextInput, UnstyledButton } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { RichTextField } from "@/components/RichTextField";

// Bir dilin çeviri alanları. AYNI ANDA TEK DİL gösteriliyor, üstteki şeritten seçilerek.
//
// Eskiden her dil için ayrı alanlar alt alta diziliyordu: dört dilde ürün formu 8 alan, on dilde 20
// oluyordu ve açıklama alanları tiptap editörü olduğu için her biri ~200px — pencere okunmaz bir
// duvara dönüyordu. Dil sayısı artık kiracının elinde (30+ katalog), yani bu düzen sürdürülebilir
// değildi.
//
// Taban dilin alanları BURADA DEĞİL: onlar formun kendi ana alanları ve her zaman görünür. Burası
// yalnızca çeviriler — kullanıcı önce tabanı yazıp "Diğer Dillere Çevir"e basıyor, sonra dil dil
// gözden geçiriyor.
//
// Bileşen bir formda BİRDEN ÇOK kez kullanılabiliyor (ürün formunda ad bölümünde bir, açıklama
// bölümünde bir): her çeviri alanı, çevirdiği taban alanın hemen altında duruyor. O durumda seçili
// dil DIŞARIDAN veriliyor (`active` + `onActiveChange`) ve şeritler birlikte hareket ediyor —
// ayrı ayrı durum tutsalardı kullanıcı ad bölümünde İngilizce, açıklama bölümünde Arapça'da
// kalabilirdi. Tek şeritli formlarda (kategori) bu propları vermek gerekmiyor, kendi durumunu tutar.

// Alanın o dilde dolu olup olmadığı. Zengin metin boşken '<p></p>' geliyor, düz boşluk kontrolü
// onu dolu sayardı ve her dil işaretli görünürdü.
const hasContent = (value) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

// Zengin metnin düz karşılığı. Çeviri alanının placeholder'ı olarak KAYNAK metin gösteriliyor:
// çevirirken en çok işe yarayan ipucu, çevrilecek cümlenin kendisi. Diller artık veriden geldiği
// için dile özel örnek metin tutmak da mümkün değildi (Fransızca'da placeholder undefined kalıyordu).
//
// Etiketleri temizlemek ZORUNLU, tercih değil: placeholder DOM'a yazılmıyor, tiptap element'e
// `data-placeholder` koyuyor ve görüneni CSS üretiyor (`content: attr(data-placeholder)`,
// bkz. index.css). CSS `content` düz string basar, içindeki HTML ayrıştırılmaz — ham değer
// verilseydi ekranda harfi harfine `<p>Taze malzemeler</p>` yazardı.
//
// Kısaltma şart: placeholder bir İPUCU, metnin tamamı değil. Uzun bir açıklama boş editörün
// gövdesini aşıyor (tiptap placeholder'ı `height: 0` bir ::before, kutuyu büyütmüyor) ve alan
// dolu sanılıyordu.
const PLACEHOLDER_MAX = 140;

const asPlainText = (value) => {
  const text = (value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return text.length > PLACEHOLDER_MAX
    ? `${text.slice(0, PLACEHOLDER_MAX).trimEnd()}…`
    : text;
};

// Taban dilin alanı DÜZ isimle duruyor (`name`, `description`) — içeriğin kendi kolonu o. Çeviriler
// dil kodu son ekiyle (`nameEN`). Bu yüzden anahtar dile göre değişiyor.
const fieldKey = (field, code, baseCode) =>
  code === baseCode ? field : `${field}${code.toUpperCase()}`;

export function TranslationFields({
  languages,
  form,
  fields,
  active,
  onActiveChange,
  baseCode,
}) {
  const [ownActive, setOwnActive] = useState(null);
  const controlled = onActiveChange !== undefined;
  const selected = controlled ? active : ownActive;
  const setSelected = controlled ? onActiveChange : setOwnActive;

  // Kiracı dil listesini değiştirdiğinde seçili dil listede kalmayabiliyor; ilkine dön.
  useEffect(() => {
    if (languages.length && !languages.some((item) => item.code === selected)) {
      setSelected(languages[0].code);
    }
  }, [languages, selected, setSelected]);

  if (!languages.length) {
    return null;
  }

  const current =
    languages.find((item) => item.code === selected) ?? languages[0];
  // Hangi dillerde en az bir alan dolu. Hem seçenek satırındaki işaret hem sayaç bundan besleniyor.
  const filledCodes = new Set(
    languages
      .filter((item) =>
        fields.some((field) =>
          hasContent(form.values[fieldKey(field.name, item.code, baseCode)]),
        ),
      )
      .map((item) => item.code),
  );

  return (
    <Stack gap="sm">
      {/* Çip iki AYRI şeyi birden söylüyor ve ikisi karışmamalı:
                  hangisini düzenliyorum → dolgu rengi (seçili olan koyu)
                  hangisi çevrilmiş     → kenarlık ve ikon
                Yeşil bir nokta yeterince okunmuyordu: küçük, sessiz ve "ne demek" belirsizdi.
                KESİK kenarlık + soluk metin "burası boş" demenin en doğrudan yolu — liste
                tarandığında eksikler kendiliğinden sırıtıyor, ikonu okumaya bile gerek kalmıyor. */}
      <Group gap={6} wrap="wrap">
        {languages.map((item) => {
          const filled = filledCodes.has(item.code);
          const isCurrent = item.code === current.code;

          return (
            <UnstyledButton
              key={item.code}
              onClick={() => setSelected(item.code)}
              px="sm"
              py={5}
              title={
                filled
                  ? `${item.nativeLabel} — çevrildi`
                  : `${item.nativeLabel} — boş`
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                borderRadius: "var(--mantine-radius-sm)",
                border: filled
                  ? "1px solid var(--mantine-color-teal-4)"
                  : "1px dashed var(--mantine-color-gray-4)",
                background: isCurrent
                  ? "var(--mantine-color-chocolate-0)"
                  : "transparent",
                outline: isCurrent
                  ? "2px solid var(--mantine-color-chocolate-4)"
                  : "none",
                outlineOffset: -1,
                color: filled
                  ? "var(--mantine-color-text)"
                  : "var(--mantine-color-dimmed)",
                fontSize: 13,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <span>{item.flag}</span>
              <span>{item.label}</span>
              {/* Yalnızca BOŞ olanda işaret: dolu diller çoğunlukta ve her birine tik
                                basmak şeridi gürültüye çeviriyordu. Eksik olan dikkat çeksin yeter —
                                kesik çerçeve ve soluk metin de aynı şeyi söylüyor. */}
              {!filled && <IconPencil size={13} opacity={0.5} />}
            </UnstyledButton>
          );
        })}
      </Group>

      {fields.map((field) => {
        const key = fieldKey(field.name, current.code, baseCode);
        // Taban dilde sabit bir örnek; çeviride KAYNAK metnin kendisi — çevirirken en çok
        // işe yarayan ipucu çevrilecek cümle.
        const source =
          current.code === baseCode
            ? (field.placeholder ?? "")
            : asPlainText(form.values[field.name]);

        return field.rich ? (
          <RichTextField
            key={key}
            label={`${field.label} (${current.label})`}
            placeholder={source}
            value={form.values[key]}
            onChange={(value) => form.setFieldValue(key, value)}
          />
        ) : (
          <TextInput
            key={key}
            label={`${field.label} (${current.label})`}
            placeholder={source}
            leftSection={current.flag}
            {...form.getInputProps(key)}
          />
        );
      })}
    </Stack>
  );
}
